import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Send, Timer, Flag, Shuffle, TrendingUp, Award, Sparkles } from "lucide-react";
import { api, API_URL, extractApiError } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { GameBoard } from "@/components/GameBoard";
import { LetterTile } from "@/components/LetterTile";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { HelpButton } from "@/components/HelpButton";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { celebrateWin } from "@/lib/confetti";

const LETTER_VALUES = {
  A: 1, E: 1, I: 1, L: 1, N: 1, O: 1, R: 1, S: 1, T: 1, U: 1,
  D: 2, G: 2, M: 2, B: 3, C: 3, P: 3, F: 4, H: 4, V: 4,
  J: 8, Q: 8, K: 10, W: 10, X: 10, Y: 10, Z: 10, "?": 0,
};

function getWsUrl(gameId, token) {
  if (!API_URL) return null;
  const u = new URL(API_URL);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}/api/games/${gameId}/ws?token=${encodeURIComponent(token)}`;
}

export default function GameRoom() {
  const { gameId } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [bonuses, setBonuses] = useState([]);
  const [plateau, setPlateau] = useState([]);
  const [center, setCenter] = useState([2, 4]);
  const [placed, setPlaced] = useState([]); // {row, col, letter, is_blank, rackIndex}
  const [selectedRack, setSelectedRack] = useState(null);
  const [remaining, setRemaining] = useState(40);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatText, setChatText] = useState("");
  const [showForfeit, setShowForfeit] = useState(false);
  const [celebrated, setCelebrated] = useState(false);
  const wsRef = useRef(null);

  const fetchGame = async () => {
    try {
      const { data } = await api.get(`/games/${gameId}`);
      setGame(data);
    } catch (e) {
      toast.error(extractApiError(e));
      navigate("/play");
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const b = await api.get("/games/bonuses");
        setBonuses(b.data.bonuses);
        setPlateau(b.data.plateau || []);
        setCenter(b.data.center || [2, 4]);
      } catch (_) {}
      await fetchGame();
    })();
  }, [gameId]);

  // Websocket
  useEffect(() => {
    if (!game || !user) return;
    // We need a token to authenticate WS. We rely on cookie-side session
    // via a query token. Fetch a short-lived one from /auth/me and reuse
    // the session/access as query param.
    const openWs = async () => {
      let token = "";
      try {
        const r = await fetch(`${API_URL}/api/auth/refresh`, { method: "POST", credentials: "include" });
        // We can't read HTTP-only cookies. So use document.cookie fallback (which is empty). 
        // Simplest: pass a signed token via extra endpoint? We'll use polling as backup.
      } catch (_) {}
      // Use polling as fallback (no WS auth possible without exposing token)
    };
    openWs();
    const int = setInterval(fetchGame, 3000);
    return () => clearInterval(int);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.game_id, user?.user_id]);

  // Timer
  useEffect(() => {
    if (!game || game.status !== "active" || !game.turn_started_at) return;
    const started = new Date(game.turn_started_at).getTime();
    const total = game.turn_seconds || 40;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - started) / 1000);
      const rem = Math.max(0, total - elapsed);
      setRemaining(rem);
      if (rem === 0) {
        // Trigger backend timeout check
        api.post(`/games/${game.game_id}/timeout`).catch(() => {});
      }
    };
    tick();
    const int = setInterval(tick, 500);
    return () => clearInterval(int);
  }, [game?.turn_started_at, game?.status]);

  // Auto-trigger bot move when it's the bot's turn
  useEffect(() => {
    if (!game || game.mode !== "bot" || game.status !== "active") return;
    const current = game.players[game.current_turn];
    if (!current || current.user_id !== "bot_globo_official") return;
    const timer = setTimeout(() => {
      api.post(`/games/${game.game_id}/bot-move`).then(({ data }) => {
        setGame(data);
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(timer);
  }, [game?.current_turn, game?.status, game?.mode, game?.game_id]);

  // Confetti on win — fire once when game transitions to over
  useEffect(() => {
    if (!game) return;
    const isOver = game.status === "finished" || game.status === "abandoned";
    if (isOver && !celebrated && game.winner_id === user?.user_id) {
      setCelebrated(true);
      celebrateWin();
    }
  }, [game, celebrated, user?.user_id]);

  // Assisted first game — fetch user stats to determine if beginner
  const [beginner, setBeginner] = useState(false);
  useEffect(() => {
    api.get("/stats/me").then(({ data }) => {
      setBeginner((data.games_played ?? 0) < 5);
    }).catch(() => {});
  }, []);

  if (!game) {
    return <AppLayout><div className="py-20 text-center text-muted-foreground">{t("common.loading")}</div></AppLayout>;
  }

  const me = game.players.find((p) => p.user_id === user.user_id);
  const opp = game.players.find((p) => p.user_id !== user.user_id);
  const isMyTurn = game.players[game.current_turn]?.user_id === user.user_id;
  const myRack = me?.rack || [];

  // Rack minus placed tiles (by index of first match)
  const usedIndices = new Set(placed.map((p) => p.rackIndex));
  const displayRack = myRack.map((letter, idx) => ({ letter, idx, used: usedIndices.has(idx) }));

  const clickRackTile = (idx) => {
    if (!isMyTurn) return;
    setSelectedRack(idx === selectedRack ? null : idx);
  };

  const clickBoardCell = (r, c) => {
    if (!isMyTurn) return;
    // If a placed tile is here, remove it (send back to rack)
    const existing = placed.find((p) => p.row === r && p.col === c);
    if (existing) {
      setPlaced(placed.filter((p) => p !== existing));
      return;
    }
    if (game.board[r][c]) return;
    if (selectedRack == null) return;
    const rackLetter = myRack[selectedRack];
    if (rackLetter == null) return;
    let letter = rackLetter;
    let isBlank = rackLetter === "?";
    if (isBlank) {
      const chosen = window.prompt("Choisissez une lettre pour le joker (A-Z):", "A");
      if (!chosen) return;
      letter = chosen.trim().toUpperCase().slice(0, 1);
      if (!/[A-Z]/.test(letter)) return;
    }
    setPlaced([...placed, { row: r, col: c, letter, is_blank: isBlank, rackIndex: selectedRack }]);
    setSelectedRack(null);
  };

  const recall = () => setPlaced([]);

  const submitMove = async () => {
    if (placed.length === 0) return;
    try {
      const { data } = await api.post(`/games/${game.game_id}/move`, {
        tiles: placed.map(({ row, col, letter, is_blank }) => ({ row, col, letter, is_blank })),
      });
      setGame(data);
      setPlaced([]);
      setSelectedRack(null);
      toast.success("Mot joué");
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const passTurn = async () => {
    try {
      const { data } = await api.post(`/games/${game.game_id}/move`, { pass: true });
      setGame(data);
      setPlaced([]);
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const forfeit = async () => {
    try {
      const { data } = await api.post(`/games/${game.game_id}/forfeit`);
      setGame(data);
      setShowForfeit(false);
      toast.success(t("game.over"));
    } catch (e) {
      toast.error(extractApiError(e));
    }
  };

  const gameOver = game.status === "finished" || game.status === "abandoned";
  const waiting = game.status === "waiting";
  const isTimerWarn = remaining <= 10 && isMyTurn && game.status === "active";

  return (
    <AppLayout>
      {/* Assisted hint for beginners */}
      {beginner && game.status === "active" && !gameOver && (
        <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-start gap-3 animate-float-in" data-testid="assist-hint">
          <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-primary">Coach Globo :</span>{" "}
            {game.moves.length === 0
              ? t("assist.firstMove")
              : isMyTurn && placed.length === 0
                ? t("assist.selectTile")
                : isMyTurn && placed.length > 0
                  ? t("assist.playWord")
                  : t("assist.wait")}
          </div>
        </div>
      )}

      {/* Header / Status */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {waiting ? t("game.waiting") : (isMyTurn ? t("game.turn") : t("game.opponent"))}
          </h1>
          {game.invite_code && waiting && (
            <div className="mt-2 flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t("game.invite")}:</span>
              <Badge className="font-mono text-lg tracking-widest" data-testid="game-invite-code">{game.invite_code}</Badge>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          {!gameOver && (
            <Button variant="outline" size="sm" onClick={() => setShowForfeit(true)} data-testid="game-forfeit-btn">
              <Flag className="w-4 h-4 mr-1" /> {t("game.forfeit")}
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 items-start">
        {/* Left: me */}
        <PlayerCard player={me} label="Vous" active={isMyTurn && game.status === "active"} />

        {/* Board */}
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <Timer className="w-4 h-4" />
            <span className={`font-mono tabular-nums ${isTimerWarn ? "timer-warning font-bold" : ""}`} data-testid="game-timer">
              {isMyTurn && game.status === "active" ? `${remaining}s` : "—"}
            </span>
            <span className="text-xs text-muted-foreground">/ {game.turn_seconds}s</span>
          </div>
          <GameBoard
            board={game.board}
            bonuses={bonuses}
            plateau={plateau}
            center={center}
            placed={placed}
            onCellClick={clickBoardCell}
            size={52}
          />
          {/* Rack */}
          {me && (
            <div className="flex items-center gap-2 flex-wrap justify-center" data-testid="game-rack">
              {displayRack.map(({ letter, idx, used }) => (
                <LetterTile
                  key={idx}
                  letter={letter}
                  points={LETTER_VALUES[letter]}
                  size="lg"
                  selected={selectedRack === idx}
                  faded={used}
                  onClick={used ? undefined : () => clickRackTile(idx)}
                  testId={`rack-tile-${idx}`}
                />
              ))}
            </div>
          )}
          {/* Actions */}
          <div className="flex flex-wrap gap-2 justify-center">
            <Button variant="outline" size="sm" onClick={recall} disabled={placed.length === 0}
              data-testid="game-recall-btn">
              <X className="w-4 h-4 mr-1" /> {t("game.recall")}
            </Button>
            <Button variant="outline" size="sm" onClick={passTurn} disabled={!isMyTurn || game.status !== "active"}
              data-testid="game-pass-btn">
              {t("game.pass")}
            </Button>
            <Button size="sm" onClick={submitMove} disabled={!isMyTurn || placed.length === 0 || game.status !== "active"}
              data-testid="game-play-btn">
              <Send className="w-4 h-4 mr-1" /> {t("game.play")}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">{t("game.bag")}: {game.bag?.length ?? 0}</div>
        </div>

        {/* Right: opponent */}
        <PlayerCard player={opp} label={opp?.name || "…"} active={!isMyTurn && game.status === "active" && !!opp} isOpponent />
      </div>

      {gameOver && <PostGameAnalysis game={game} user={user} navigate={navigate} t={t} />}

      {/* Move history */}
      {game.moves?.length > 0 && (
        <Card className="border-border mt-8">
          <CardHeader><CardTitle className="font-display text-base">Historique de la partie</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm divide-y divide-border">
              {game.moves.slice(-10).reverse().map((m, i) => {
                const p = game.players.find((pp) => pp.user_id === m.player_id);
                return (
                  <div key={i} className="py-2 flex items-center justify-between">
                    <span className="text-muted-foreground">{p?.name}</span>
                    <span className="font-mono">{m.passed ? "PASS" : m.word.toUpperCase()}</span>
                    <span className="font-semibold tabular-nums">{m.score}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showForfeit} onOpenChange={setShowForfeit}>
        <DialogContent data-testid="forfeit-dialog">
          <DialogHeader>
            <DialogTitle>{t("game.forfeitConfirm")}</DialogTitle>
            <DialogDescription>Cette action ne peut pas être annulée.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForfeit(false)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={forfeit} data-testid="forfeit-confirm-btn">{t("common.confirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function PlayerCard({ player, label, active, isOpponent }) {
  return (
    <Card className={`border-border ${active ? "ring-2 ring-primary" : ""}`}>
      <CardContent className="p-6">
        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">{label}</div>
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12">
            <AvatarImage src={player?.picture} />
            <AvatarFallback>{(player?.name || "?").slice(0,2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-semibold">{player?.name || "En attente..."}</div>
            <div className="text-3xl font-display font-black tabular-nums">{player?.score ?? 0}</div>
          </div>
        </div>
        {isOpponent && player && (
          <div className="mt-4 text-xs text-muted-foreground">
            {player.rack?.length ?? 0} lettres
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PostGameAnalysis({ game, user, navigate, t }) {
  const me = game.players.find((p) => p.user_id === user.user_id);
  const opp = game.players.find((p) => p.user_id !== user.user_id);
  const won = game.winner_id === user.user_id;
  const isDraw = !game.winner_id && game.status === "finished";

  const myMoves = (game.moves || []).filter((m) => m.player_id === user.user_id && !m.passed);
  const bestMove = myMoves.reduce((b, m) => (!b || m.score > b.score ? m : b), null);
  const avgPerWord = myMoves.length ? Math.round(myMoves.reduce((s, m) => s + m.score, 0) / myMoves.length) : 0;
  const bingoCount = myMoves.filter((m) => (m.tiles?.length ?? 0) === 7).length;

  const tips = [];
  if (avgPerWord < 12) tips.push(t("pg.tip.longWords"));
  if (bingoCount === 0) tips.push(t("pg.tip.bingo"));
  if (myMoves.length && !myMoves.some((m) => m.tiles?.some((tt) => tt.row === 2 && tt.col === 4))) tips.push(t("pg.tip.center"));
  tips.push(t("pg.tip.regular"));

  const strengths = [];
  if (bingoCount > 0) strengths.push(`✨ ${bingoCount} Bingo`);
  if (bestMove) strengths.push(`🏆 ${bestMove.word?.toUpperCase()} (+${bestMove.score})`);
  if (myMoves.length >= 5) strengths.push(`⚡ ${myMoves.length} coups joués`);

  const resultLabel = won ? `🏆 ${t("history.won")}` : isDraw ? t("game.draw") : t("history.lost");

  return (
    <Card className="border-border mt-8 max-w-3xl mx-auto animate-float-in" data-testid="game-over-panel">
      <CardHeader className="pb-4">
        <CardTitle className="font-display text-3xl tracking-tight">
          {t("game.over")} — <span className={won ? "text-primary" : ""}>{resultLabel}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Scores */}
        <div className="grid grid-cols-2 gap-4 text-center mb-6">
          {game.players.map((p) => (
            <div key={p.user_id} className={`p-4 rounded-xl border ${p.user_id === game.winner_id ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{p.name}</div>
              <div className="text-4xl font-display font-black">{p.score}</div>
            </div>
          ))}
        </div>

        {/* Analysis */}
        <div className="border-t border-border pt-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="font-display font-bold text-lg">{t("pg.title")}</h3>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="p-3 rounded-lg bg-secondary text-center">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pg.wordsPlayed")}</div>
              <div className="text-2xl font-display font-extrabold" data-testid="pg-words-count">{myMoves.length}</div>
            </div>
            <div className="p-3 rounded-lg bg-secondary text-center">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pg.bestWord")}</div>
              <div className="text-lg font-display font-bold" data-testid="pg-best-word">
                {bestMove ? `${bestMove.word?.toUpperCase()} (${bestMove.score})` : "—"}
              </div>
            </div>
            <div className="p-3 rounded-lg bg-secondary text-center">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("pg.avgPerWord")}</div>
              <div className="text-2xl font-display font-extrabold" data-testid="pg-avg-word">{avgPerWord}</div>
            </div>
          </div>

          {strengths.length > 0 && (
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                <Award className="w-3.5 h-3.5" /> {t("pg.strengths")}
              </div>
              <div className="flex flex-wrap gap-2">
                {strengths.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-sm py-1" data-testid={`pg-strength-${i}`}>{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {tips.length > 0 && (
            <div className="mb-6">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> {t("pg.improve")}
              </div>
              <ul className="space-y-1.5 text-sm">
                {tips.slice(0, 3).map((tip, i) => (
                  <li key={i} className="flex gap-2 text-muted-foreground" data-testid={`pg-tip-${i}`}>
                    <span className="text-primary flex-shrink-0">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 border-t border-border pt-6">
          <Button onClick={() => navigate("/play")} data-testid="game-newgame-btn">
            <Sparkles className="w-4 h-4 mr-1" /> {t("pg.rematch")}
          </Button>
          <Button variant="outline" onClick={() => navigate("/history")} data-testid="game-history-btn">
            {t("nav.history")}
          </Button>
          <Button variant="outline" onClick={() => navigate("/stats")} data-testid="game-stats-btn">
            {t("nav.stats")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
